import React, { useEffect, useState, createContext, useContext } from 'react';
import { CartItemType } from '../types/types';
const CART_STORAGE_KEY = 'cart_items';
interface CartContextType {
  items: CartItemType[];
  isCartOpen: boolean;
  addItem: (item: CartItemType) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, delta: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  cartTotal: number;
  cartItemCount: number;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [items, setItems] = useState<CartItemType[]>(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.error('Error restoring cart:', e);
    }
    return [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  // Persist cart to localStorage whenever items change
  useEffect(() => {
    try {
      if (items.length > 0) {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
      } else {
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    } catch (e) {
      console.error('Error saving cart:', e);
    }
  }, [items]);
  const addItem = (item: CartItemType) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(i => i.id === item.id);
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = item;
        return newItems;
      }
      return [...prev, item];
    });
  };
  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };
  const updateQuantity = (id: string, delta: number) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return {
          ...item,
          quantity: newQuantity
        };
      }
      return item;
    }));
  };
  const clearCart = () => {
    setItems([]);
  };
  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };
  const cartTotal = items.reduce((sum, item) => sum + item.subtotal * item.quantity, 0);
  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  return <CartContext.Provider value={{
    items,
    isCartOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    toggleCart,
    cartTotal,
    cartItemCount
  }} data-id="element-1315">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}